import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';
import { deleteJobs, fetchJobs } from '../services/scraperService';

export function getStatusColor(status) {
    switch (status) {
        case 'completed': return 'success';
        case 'running': return 'info';
        case 'failed': return 'error';
        case 'pending': return 'warning';
        default: return 'default';
    }
}

// Filter logs based on level and search term
function filterLogs(logs, logFilters) {
    if (!logs) return '';
    let filteredLogs = logs;
    if (logFilters.level) {
        const level = logFilters.level.toUpperCase();
        filteredLogs = filteredLogs.split('\n').filter((line) => line.toUpperCase().includes(level)).join('\n');
    }
    if (logFilters.search) {
        const searchTerm = logFilters.search.toLowerCase();
        filteredLogs = filteredLogs.split('\n').filter((line) => line.toLowerCase().includes(searchTerm)).join('\n');
    }
    return filteredLogs;
}

export function useScraperMonitoring() {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedExecution, setSelectedExecution] = useState(null);
    const [openLogDialog, setOpenLogDialog] = useState(false);
    const [detailView, setDetailView] = useState('logs'); // 'logs', 'metrics', 'structured'
    const [logFilters, setLogFilters] = useState({ level: '', search: '' });
    const [filters, setFilters] = useState({ jobId: '', status: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [liveLogs, setLiveLogs] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const logContainerRef = useRef(null);

    const [selectedIds, setSelectedIds] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Auto-scroll logs to bottom
    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [selectedExecution?.logs, liveLogs, detailView]);

    const { data: executionsData, isLoading, isError, refetch } = useQuery(
        ['executions', filters, page, pageSize],
        () => fetchJobs({ page, pageSize, jobId: filters.jobId, status: filters.status }),
        {
            refetchInterval: 2000, // Auto-refresh every 2 seconds
            refetchIntervalInBackground: true,
            keepPreviousData: true,
        }
    );

    // Update selectedExecution if it's open and its data changes in the main query.
    // The list only carries lightweight summary fields - full logs/metrics/structured_logs
    // are fetched once via handleViewLogs and kept live via the SSE stream while running -
    // so only status/product-count fields are refreshed here to avoid clobbering the
    // already-loaded full logs with the list's placeholder log text.
    useEffect(() => {
        if (openLogDialog && selectedExecution?.id && executionsData?.items) {
            const updatedExecution = executionsData.items.find((e) => e.id === selectedExecution.id);
            if (updatedExecution) {
                setSelectedExecution((prev) => {
                    if (!prev) return updatedExecution;

                    const hasStatusChanged = prev.status !== updatedExecution.status;
                    const hasProductsChanged = prev.products_found !== updatedExecution.products_found;

                    if (!hasStatusChanged && !hasProductsChanged) {
                        return prev;
                    }

                    return {
                        ...prev,
                        status: updatedExecution.status,
                        products_found: updatedExecution.products_found,
                        completed_at: updatedExecution.completed_at,
                    };
                });
            }
        }
    }, [executionsData, openLogDialog, selectedExecution?.id]);

    const { data: alertsData = [] } = useQuery(
        'alerts',
        () => api.get('/api/alerts').then((res) => res.data),
        { refetchInterval: 60000 }
    );

    const { data: jobs = [] } = useQuery('jobs', () => api.get('/api/jobs').then((res) => res.data));

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Function to stop live log streaming
    const stopLiveLogStreaming = () => {
        if (selectedExecution && selectedExecution.eventSource) {
            selectedExecution.eventSource.close();
            setIsStreaming(false);
        }
    };

    // Function to start live log streaming
    const startLiveLogStreaming = (executionId) => {
        // Stop any existing streaming
        if (isStreaming) {
            stopLiveLogStreaming();
        }

        // Create an EventSource to stream logs
        const eventSource = new EventSource(`${process.env.REACT_APP_API_URL || ''}/api/executions/${executionId}/live-logs`);
        setIsStreaming(true);
        setLiveLogs('');

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.done) {
                    eventSource.close();
                    setIsStreaming(false);
                    setSelectedExecution((prev) => prev && ({
                        ...prev,
                        status: data.status,
                        logs: prev.logs + (data.logs || ''),
                    }));
                } else {
                    if (data.is_new) {
                        setLiveLogs((prev) => prev + (data.logs || ''));
                    } else {
                        setLiveLogs(data.logs || '');
                    }
                }
            } catch (error) {
                console.error('Error parsing log event:', error);
            }
        };

        eventSource.onerror = (error) => {
            console.error('Error in log streaming:', error);
            eventSource.close();
            setIsStreaming(false);
        };

        // Store the event source so we can close it later
        setSelectedExecution((prev) => ({ ...prev, eventSource }));
    };

    const handleViewLogs = async (execution) => {
        try {
            // The list view omits full logs/structured_logs (they can be many MB for
            // long-running scrapes), so fetch the full record here on demand.
            const response = await api.get(`/api/executions/${execution.id}`);
            setSelectedExecution({ ...execution, ...response.data });
        } catch (error) {
            console.error('Error fetching execution details:', error);
            setSelectedExecution(execution);
        } finally {
            setDetailView('logs');
            setLogFilters({ level: '', search: '' });
            setOpenLogDialog(true);
            if (execution.status === 'running') {
                startLiveLogStreaming(execution.id);
            }
        }
    };

    // Function to stop a running job
    const stopJob = async (executionId) => {
        try {
            await api.post(`/api/executions/${executionId}/stop`);
            showSnackbar('Execution stopped successfully', 'success');
            refetch();
        } catch (error) {
            console.error('Error stopping execution:', error);
            showSnackbar('Failed to stop execution', 'error');
        }
    };

    // Clean up streaming when component unmounts
    useEffect(() => {
        return () => {
            if (isStreaming) {
                stopLiveLogStreaming();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStreaming]);

    const handleCloseLogDialog = () => {
        stopLiveLogStreaming();
        setOpenLogDialog(false);
        setSelectedExecution(null);
        setLiveLogs('');
    };

    const handleRefresh = () => {
        refetch();
        showSnackbar('Data refreshed');
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Compute visible executions (used for "select all")
    const visibleExecutions = useMemo(() => executionsData?.items || [], [executionsData]);
    const visibleIds = useMemo(() => visibleExecutions.map((e) => e.id), [visibleExecutions]);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

    const handleToggleSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleToggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
        } else {
            setSelectedIds((prev) => {
                const set = new Set(prev);
                visibleIds.forEach((id) => set.add(id));
                return Array.from(set);
            });
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0) return;
        const confirmed = window.confirm(`Delete ${selectedIds.length} selected execution(s)? This action cannot be undone.`);
        if (!confirmed) return;

        setIsDeleting(true);
        try {
            await deleteJobs(selectedIds);
            await refetch();
            setSelectedIds([]);
            showSnackbar(`Deleted ${selectedIds.length} execution(s)`, 'success');
        } catch (error) {
            console.error('Error deleting jobs:', error);
            showSnackbar('Failed to delete selected executions', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const filteredSelectedExecutionLogs = selectedExecution?.logs ? filterLogs(selectedExecution.logs, logFilters) : '';

    return {
        // Tabs / dialog state
        activeTab,
        setActiveTab,
        selectedExecution,
        openLogDialog,
        detailView,
        setDetailView,
        logFilters,
        setLogFilters,
        filteredSelectedExecutionLogs,
        // Filters / pagination
        filters,
        setFilters,
        page,
        setPage,
        pageSize,
        setPageSize,
        // Data
        executionsData,
        isLoading,
        isError,
        alertsData,
        jobs,
        visibleExecutions,
        visibleIds,
        // Selection
        selectedIds,
        isDeleting,
        allSelected,
        handleToggleSelect,
        handleToggleSelectAll,
        handleDeleteSelected,
        // Log streaming
        liveLogs,
        isStreaming,
        logContainerRef,
        // Snackbar
        snackbar,
        handleCloseSnackbar,
        // Handlers
        handleViewLogs,
        handleCloseLogDialog,
        handleRefresh,
        stopJob,
    };
}
